/* components/SiteHeader.ts
Global Site Header

Example
<site-header>
    Site header content here
</site-header>
*/

import { AnnouncementBar } from '../AnnouncementBar/AnnouncementBar';
import { Component, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { debounce } from 'lodash';
import { SideCart } from '../SideCart/SideCart';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';

@Component
export class SiteHeader extends ThemeComponent {
    @Ref() row!: HTMLDivElement;
    @Ref({required: false}) cartTrigger!: HTMLDivElement;
    @Ref() menuToggle!: HTMLButtonElement;
    @Ref({required: false}) count!: HTMLElement;
    @Ref({ required: false }) announcement!: AnnouncementBar;
    @Ref({ required: false }) childList!: HTMLUListElement[];
    @Ref({ required: false }) childItems!: HTMLLIElement[];
    @Ref({ required: false }) grandchildContainers!: HTMLDivElement[];
    @Ref({ required: false }) childWrappers!: HTMLDivElement[];

    private _onOpenChange = new SimpleEvent<string>();
    private sideCart:SideCart|null = document.body.querySelector('side-cart');
    private initalActiveChild!: HTMLLIElement;

    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    public changeOpen(open: string) {
        // Use the private backing field to dispatch
        this._onOpenChange.dispatch('');
    }

    init() {
        this.setEventListeners();
        document.addEventListener('cartChanged', (e: Event) => {
            this.updateCartCount();
        });
    }

    /**
     * Set event listeners.
     */
    setEventListeners() {

        // Add a listener to each child menu item. When hovering
        // a child item, populate the grandchild list in the
        // corresponding menu section to the side
        if (this.childItems) {
            for (const child of this.childItems) {
                this.$listen(child, 'mouseenter', () => {
                    this.populateGrandchildMenu(child);
                });

            }

            // Hide the grandhild menu when the mouse
            // leaves the child menu
            for (const childWrapper of this.childWrappers) {
                this.$listen(childWrapper, 'mouseleave', (event) => {
                    this.hideGrandchildMenu(childWrapper);
                });
            }
        }

        this.handleScroll();
        this.cartEvent();
        this.menuEvent();
    }

    populateGrandchildMenu(child: HTMLLIElement) {
        const dropdown = child.closest('.megamenu__dropdown-container');
        const activeItem = dropdown?.querySelector('.megamenu__child--active');
        activeItem?.classList.remove('megamenu__child--active');
        if (child.classList.contains('megamenu__child--parent')) {
            child.classList.add('megamenu__child--active');
            // Get the child list
            const grandchildList = child.querySelector('.megamenu__grandchild-list');
            const grandchildContainer = dropdown?.querySelector('.megamenu__grandchild-menu');
            if (dropdown && grandchildList && grandchildContainer) {
                dropdown.classList.add('grandchild-active');
                grandchildContainer.innerHTML = grandchildList.outerHTML;
            }
        } else {
            if (dropdown) {
                dropdown.classList.remove('grandchild-active');
            }
        }
    }

    hideGrandchildMenu(childWrapper: HTMLDivElement) {
        const dropdown = childWrapper.closest('.megamenu__dropdown-container');
        if (dropdown) {
            dropdown.classList.remove('grandchild-active');
            const activeItem = dropdown?.querySelector('.megamenu__child--active');
            activeItem?.classList.remove('megamenu__child--active');
        }
    }


    private handleScroll() {
        const headerHeight = this.offsetHeight;
        const header = this.closest('header');
        let height = 0;
        window.addEventListener('scroll', (e) => {
            if (window.scrollY > headerHeight && window.scrollY > height) {
                header?.classList.add('site-header--down');
                header?.classList.remove('site-header--up');
                if (this.announcement) {
                    this.announcement.classList.remove('visually-hidden');
                }
            } else if (window.scrollY > headerHeight && window.scrollY < height) {
                header?.classList.add('site-header--up');
                header?.classList.remove('site-header--down');
                if (this.announcement) {
                    this.announcement.classList.add('visually-hidden');
                }
            } else if (window.scrollY < headerHeight) {
                header?.classList.remove('site-header--down');
                header?.classList.remove('site-header--up');
                if (this.announcement) {
                    this.announcement.classList.remove('visually-hidden');
                }
            }
            height = window.scrollY;
        });
    }
    /**
     * Cart Close click event.
     * @returns
     */
    cartEvent() {
        if (!this.cartTrigger) { return; }
        this.$listen(this.cartTrigger, 'click', (event) => {
            event.preventDefault();

            if (!this.sideCart) { return; }
            this.sideCart.open();
        });
    }

    menuEvent() {
        this.$listen(this.menuToggle, 'click', () => {
            this.handleDrawerOpen();
        });
    }

    /**
     * Update cart count
     * @returns
     */
    private updateCartCount() {
        const count = cart.getCartItemCount();
        this.count.innerHTML = String(count);
        if (count > 0) {
            this.count.parentElement?.classList.add('is-active');
        } else {
            this.count.parentElement?.classList.remove('is-active');
        }
    }

    /**
     * Trigger menu drawer.
     * @returns
     */
    handleDrawerOpen() {
        if (window.innerWidth > 1200) { return; }
        const menu = document.querySelector('.mobile-menu');
        const overlay = document.querySelector('.mobile-menu__overlay');
        document.documentElement.classList.add('drawer-open');
        document.body.classList.add('drawer-open');
        menu?.classList.add('is-active');
        overlay?.classList.add('is-active');
    }
}
